import React, { useEffect } from 'react';

import Modal from 'react-modal';

import { SubscriptionTier } from '@invoice-simple/common';

import { useISIntl } from 'src/i18n';
import UserModel, { Cadence } from 'src/models/UserModel';
import { BookkeepingTier, getBookkeepingPrice } from 'src/util/subscriptions';
import { titleizeAll } from 'src/util/Titleize';
import { getBookkeepingTierPrice, getFormattedCurrency } from '../../utils';
import { messages } from './translations';

const BOOKKEEPING_TIERS: BookkeepingTier[] = [
  SubscriptionTier.ESSENTIALS,
  SubscriptionTier.PLUS,
  SubscriptionTier.PREMIUM
];

if (process.env.NODE_ENV !== 'test') {
  Modal.setAppElement('#app');
}

const customStyles = {
  content: {
    position: 'relative',
    border: 'none',
    background: 'none',
    inset: '0px',
    overflow: 'visible',
    padding: 'none'
  } as React.CSSProperties,
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.30)',
    zIndex: 50,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '1rem'
  } as React.CSSProperties
};

export interface BookkeepingUpsellModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  onDecline: () => void;
  currentPlanPriceCents: number;
  cadence: Cadence;
  currencyCode: string;
  tierName?: SubscriptionTier;
  hasBaseSubscription?: boolean;
}

export const BookkeepingUpsellModal: React.FC<BookkeepingUpsellModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
  onDecline,
  currentPlanPriceCents,
  cadence,
  currencyCode,
  tierName,
  hasBaseSubscription
}) => {
  const { formatMessage } = useISIntl();
  const user = UserModel.getInstance();
  const couponInfo = user.paywallCouponInfo;

  const bookkeepingPrice = getBookkeepingPrice(cadence, currencyCode);
  const isBookkeepingTier = tierName && BOOKKEEPING_TIERS.includes(tierName as BookkeepingTier);

  const { regularPrice, discountedPrice } = getBookkeepingTierPrice(
    tierName as BookkeepingTier,
    cadence,
    currencyCode,
    currentPlanPriceCents
  );

  const newTotalPrice = isBookkeepingTier ? regularPrice : currentPlanPriceCents + bookkeepingPrice;
  const formattedBookkeepingPrice = getFormattedCurrency(user, newTotalPrice / 100);

  const formattedDiscountedPrice = discountedPrice
    ? getFormattedCurrency(user, discountedPrice / 100)
    : undefined;

  const formattedBaseBookkeepingPrice = getFormattedCurrency(user, bookkeepingPrice / 100);

  const priceUnit =
    cadence === Cadence.ANNUAL ? formatMessage(messages.perYear) : formatMessage(messages.perMonth);

  const displayTierName = tierName ? titleizeAll(tierName) : 'Selected plan';
  const discountDuration = couponInfo?.durationMonths;

  useEffect(() => {
    if (isOpen) {
      UserModel.getInstance().events.trackModalView({
        action: 'viewed',
        feature: 'bookkeeping-upsell-modal',
        cadence,
        tier: tierName
      });
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} onRequestClose={onClose} style={customStyles}>
      <div id="tailwind">
        <div className="bg-white rounded-xl w-full max-w-[480px] shadow-md max-h-[90vh] overflow-y-auto">
          {/* Top bar with close button */}
          <div
            className="flex justify-end items-center h-[50px]"
            style={{ borderBottom: '1px solid #e5e7eb' }}>
            <button
              data-testid="bookkeeping-modal-close"
              className="mr-4 p-1 text-gray-500 hover:text-gray-700 border-none outline-none focus:outline-none bg-transparent"
              onClick={onClose}
              aria-label="Close">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Content area */}
          <div className="px-5 py-6">
            {/* Hero image */}
            <div className="rounded-[10px] overflow-hidden mb-[30px]">
              <img
                src="/images/bookkeeping_upsell_hero.png"
                alt="Profit and Loss Report Preview"
                className="w-full h-auto"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>

            {/* Title and description */}
            <div className="px-3">
              <h2
                className="font-semibold text-[#1f2937] mb-1"
                style={{ fontSize: '20px', lineHeight: '32px' }}>
                {hasBaseSubscription
                  ? formatMessage(messages.titleExistingPlan)
                  : formatMessage(messages.title)}
              </h2>
              <div className="text-[#212121]" style={{ fontSize: '16px', lineHeight: '24px' }}>
                <p className="mb-4">{formatMessage(messages.description)}</p>
                <p>{formatMessage(messages.descriptionSecondary)}</p>
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div
            className="px-5 pb-[25px] pt-[15px] bg-white"
            style={{ borderTop: '1px solid #e5e7eb' }}>
            {/* Price display */}
            <div className="mb-[5px] text-center">
              {discountedPrice ? (
                <>
                  <p style={{ fontSize: '16px', lineHeight: '24px' }}>
                    <span className="font-bold text-[#1f2937]">{formattedDiscountedPrice}</span>
                    <span className="font-medium text-[#4b5563]">{priceUnit}</span>
                  </p>
                  {cadence === Cadence.ANNUAL ? (
                    <>
                      <p
                        className="font-medium text-[#4b5563]"
                        style={{ fontSize: '14px', lineHeight: '20px' }}>
                        {formatMessage(messages.billedFirstYear, {
                          price: formattedBookkeepingPrice,
                          unit: priceUnit
                        })}
                      </p>
                    </>
                  ) : (
                    <>
                      <p
                        className="font-medium text-[#4b5563]"
                        style={{ fontSize: '14px', lineHeight: '20px' }}>
                        {formatMessage(messages.billedFirstMonths, {
                          price: formattedBookkeepingPrice,
                          duration: discountDuration,
                          unit: priceUnit
                        })}
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p style={{ fontSize: '16px', lineHeight: '24px' }}>
                    <span className="font-bold text-[#1f2937]">{formattedBookkeepingPrice}</span>
                    <span className="font-medium text-[#4b5563]">{priceUnit}</span>
                  </p>
                  <p
                    className="font-medium text-[#4b5563]"
                    style={{ fontSize: '16px', lineHeight: '24px' }}>
                    {formatMessage(messages.priceLabel)}
                  </p>
                </>
              )}
            </div>

            {/* CTA Button */}
            <button
              data-testid="bookkeeping-modal-upgrade"
              onClick={onUpgrade}
              className="w-full bg-[#ff5721] hover:bg-[#e64a19] text-white py-3 px-6 rounded-[6px] shadow-sm border-none">
              <span className="block font-bold" style={{ fontSize: '16px', lineHeight: '24px' }}>
                {formatMessage(messages.ctaUpgrade)}
              </span>
              <span className="block font-medium" style={{ fontSize: '16px', lineHeight: '24px' }}>
                {formatMessage(messages.ctaUpgradeSubtext, {
                  tierName: displayTierName,
                  price: formattedBaseBookkeepingPrice,
                  unit: priceUnit
                })}
              </span>
            </button>

            {/* Secondary Button - Continue without bookkeeping */}
            <button
              data-testid="bookkeeping-modal-decline"
              onClick={onDecline}
              className="w-full h-[50px] mt-[10px] bg-white text-[#555] font-medium rounded-[6px] border border-solid border-[#999]"
              style={{ fontSize: '16px', lineHeight: '18px' }}>
              {formatMessage(messages.ctaContinueWithPlanOnly, { tierName: displayTierName })}
            </button>

            {/* Terms */}
            <p
              className="text-[#757575] text-center mt-[10px] px-[10px]"
              style={{ fontSize: '12px', lineHeight: '18px' }}>
              {formatMessage(messages.terms)}
            </p>
          </div>
        </div>
      </div>
    </Modal>
  );
};
